import { renderRichText } from "@storyblok/react";
import type { Page } from "@/generated/sb";
import { ArticleItem } from "@/components/schema";
import { deleteAutoSlug } from "@/utils/autoSlug";

interface ArticleData {
  name: string;
  full_slug: string;
  content?: {
    publicationDate?: string;
    seoDescription?: string;
  };
}

interface CardGridWithFiltersSection {
  component: "cardGridWithFilters";
  prefetchedArticlesData?: {
    articles?: ArticleData[];
  };
}

interface TextHeaderSection {
  component: "textHeader";
  content?: Array<{
    component: string;
    title?: Array<{ content?: string }>;
    text?: Array<{ content?: unknown }>;
  }>;
}

export function extractPageTitle(sections: Page["sections"]): string | null {
  if (!sections) return null;

  for (const section of sections) {
    if (section.component === "textHeader") {
      const textHeader = section as unknown as TextHeaderSection;
      const sectionHeader = textHeader.content?.[0];
      if (sectionHeader?.title?.[0]?.content) {
        return sectionHeader.title[0].content;
      }
    }
  }
  return null;
}

export function extractPageDescription(sections: Page["sections"]): string | null {
  if (!sections) return null;

  for (const section of sections) {
    if (section.component === "textHeader") {
      const textHeader = section as unknown as TextHeaderSection;
      const sectionHeader = textHeader.content?.[0];
      if (sectionHeader?.text?.[0]?.content) {
        const html = renderRichText(sectionHeader.text[0].content as Parameters<typeof renderRichText>[0]);
        if (html) {
          let stripped = String(html);
          let prev: string;
          do {
            prev = stripped;
            stripped = stripped.replace(/<[^>]*>/g, "");
          } while (stripped !== prev);
          return stripped.slice(0, 300);
        }
      }
    }
  }
  return null;
}

export function extractArticles(sections: Page["sections"], baseUrl: string): ArticleItem[] {
  if (!sections) return [];

  for (const section of sections) {
    if (section.component === "cardGridWithFilters") {
      const cardGrid = section as unknown as CardGridWithFiltersSection;
      const articles = cardGrid.prefetchedArticlesData?.articles;
      if (!articles) return [];

      return articles.map((article) => {
        const cleanSlug = deleteAutoSlug(article.full_slug);
        const item: ArticleItem = {
          name: article.name,
          url: `${baseUrl}/${cleanSlug}`,
        };

        if (article.content?.publicationDate) {
          const date = new Date(article.content.publicationDate.replace(" ", "T"));
          if (!isNaN(date.getTime())) {
            item.datePublished = date.toISOString();
          }
        }

        if (article.content?.seoDescription) {
          item.description = article.content.seoDescription;
        }

        return item;
      });
    }
  }
  return [];
}
